import { FormControl, InputLabel, OutlinedInput, InputAdornment, Tooltip } from "@mui/material";
import {useRef, useEffect} from 'react';

export default function FormActuals(props) {
    
    const formatNumber = (num) => {
        if (!num) return '';
        // Remove any existing commas and non-numeric characters except decimal point
        const cleanNum = num.toString().replace(/[^\d.-]/g, '');
        // Format with commas
        return cleanNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        // Remove commas before saving to state
        const cleanValue = value.replace(/,/g, '');
        const finalValue = cleanValue === '' ? 0 : cleanValue;
        props.setSelectedFinancial({ ...props.selectedFinancial, [name]: finalValue });
    }

    // Create ref for first input
    const firstInputRef = useRef(null);

    // Focus on first input when component mounts
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }, []); // Empty dependency array means this runs once when component mounts

    return (
        <>
            <div className="formRow">
                <div className="formGroup">
                    <Tooltip title="Actual Earned Revenue for the period reported." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>Revenue</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualRevenue)}
                                onChange={onChange}
                                name='actualRevenue'
                                label="Revenue"
                                inputRef={firstInputRef}
                            />
                        </FormControl>
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Actual Cost of Goods Sold (COGS) for the period reported." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>COGS</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualCOGS)}
                                onChange={onChange}
                                name='actualCOGS'
                                label="COGS"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Actual Selling, General & Administrative (SG&A) expenses for the period reported." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>SG&A Overhead</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualOverhead)}
                                onChange={onChange}
                                name='actualOverhead'
                                label="SG&A Overhead"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
            </div>

            <div className="formRow">
                <div className="formGroup">
                    <Tooltip title="Actual non-statutory accruals (bonuses, safety, training, shop burden, etc. ). Enter as a positive number to add Accruals back into profit to normalize across companies. We understand the accounting convention is to have a negative number but not all companies accrue so we add back accruals to normalize profits.  Does not include statutory accruals (e.g. payroll taxes)." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>Accruals (enter as a positive value)</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualAccruals)}
                                onChange={onChange}
                                name='actualAccruals'
                                label="Accruals (enter as a positive value)"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Actual non-recurring increases or decreases to Net Income (i.e. claim settlement, etc.)." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>Adjustments to Net</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualAdjustmentsToNet)}
                                onChange={onChange}
                                name='actualAdjustmentsToNet'
                                label="Adjustments to Net"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Contributions to ESOP including cash, debt service, repurchase obligations and synthetic stock." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>ESOP</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualESOP)}
                                onChange={onChange}
                                name='actualESOP'
                                label="ESOP"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
            </div>

            <div className="formRow">
                <div className="formGroup">
                    <Tooltip title="Accruals for and/or quarterly payment for Taxes." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>Taxes</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualTaxes)}
                                onChange={onChange}
                                name='actualTaxes'
                                label="Taxes"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Actual Paycheck Protection Program (PPP) loans forgiven in the period reported." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>PPP</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualPPP)}
                                onChange={onChange}
                                name='actualPPP'
                                label="PPP"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Actual Employee Retention Credits received in the period reported." arrow placement="top">
                        <FormControl className="formControl">
                            <InputLabel>ERC</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                value={formatNumber(props.selectedFinancial.actualERC)}
                                onChange={onChange}
                                name='actualERC'
                                label="ERC"
                            />
                        </FormControl>
                    </Tooltip>
                </div>
            </div>
        </>
    );
}